import { useEffect, useState } from 'react';
import { chatApi } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Users, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface User {
  id: string;
  email: string;
  displayName: string;
  internalReferenceId: string;
}

interface ChatVisibilityFieldProps {
  value: string[];  // Array of internalReferenceIds
  onChange: (value: string[]) => void;
}

export function ChatVisibilityField({ value = [], onChange }: ChatVisibilityFieldProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    loadUsers();
  }, []);
  
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await chatApi.getVisibilityUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users for visibility selection');
    } finally {
      setLoading(false);
    }
  };
  
  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleToggle = (internalReferenceId: string) => {
    const newValue = value.includes(internalReferenceId)
      ? value.filter(id => id !== internalReferenceId)
      : [...value, internalReferenceId];
    onChange(newValue);
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          Visible to
        </label>
        {value.length === 0 ? (
          <span className="text-xs text-muted-foreground font-medium">
            Owner only
          </span>
        ) : (
          <span className="text-xs text-green-600 dark:text-green-400 font-medium">
            ✓ {value.length} user{value.length !== 1 ? 's' : ''} selected
          </span>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground">
        Select additional users who can access this in the MFE. As the owner, you always have access.
      </p>
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by email or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>
      
      {/* User List */}
      <ScrollArea className="h-[220px] border rounded-md bg-muted/20">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No users found
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center space-x-2 p-2 hover:bg-muted rounded-md cursor-pointer transition-colors"
                onClick={() => handleToggle(user.internalReferenceId)}
              >
                <Checkbox
                  checked={value.includes(user.internalReferenceId)}
                  onCheckedChange={() => handleToggle(user.internalReferenceId)}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {user.email}
                  </div>
                  {user.displayName && user.displayName !== user.email && (
                    <div className="text-xs text-muted-foreground truncate">
                      {user.displayName}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
